"use client";

import React, {
  useActionState,
  useEffect,
  useState,
  startTransition,
} from "react";
import { CMSData } from "@/cms/models";
import { ApplicationFraction } from "@/utils/gql/graphql";
import FormLayout from "../inputs/FormLayout";
import StoreSelect from "../inputs/StoreSelect";
import TextAreaInput from "../inputs/TextAreaInput";
import TextInput from "../inputs/TextInput";
import { ASSESSMENT_INITIAL_STATE } from "@/services/form.service";
import ConfirmModal from "../ConfirmModal";
import { submitAssessmentForm } from "@/actions/assessment-form";
import DateInput from "../inputs/DateInput";
import { formatPhoneNumberObject } from "@/utils/helper";

export interface AssessmentFormProps {
  config: {
    cmsData: CMSData;
    app: ApplicationFraction;
    messageTextPre?: string;
    messageTextPost?: string;
    confirmDescription: string;
    confirmTitle: string;
    resourceGroupId: string;
  };
  recaptchaSiteKey: string;
}

const AssessmentForm: React.FC<AssessmentFormProps> = ({
  config,
  recaptchaSiteKey,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isInvalidCaptchaOpen, setIsInvalidCaptchaOpen] = useState(false);
  const [formState, formAction, isPending] = useActionState(
    submitAssessmentForm,
    ASSESSMENT_INITIAL_STATE
  );

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.location.hash === "#assessment-form"
    ) {
      const element = document.getElementById("assessment-form");
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, []);

  const submit = (payload: FormData) => {
    (async () => {
      try {
        // const token = await executeRecaptcha("AssessmentForm");
        const token = await grecaptcha.enterprise.execute(recaptchaSiteKey, {
          action: "AssessmentForm",
        });

        payload.append("recaptchaToken", token);
      } catch (error) {
        console.log("[ReCaptcha Error]:", error);
        payload.delete("recaptchaToken");
      } finally {
        startTransition(() => {
          formAction(payload);
        });
      }
    })();
  };

  useEffect(() => {
    if (!("recaptchaIsValid" in formState)) {
      return;
    }

    console.log("Recaptcha:", {
      enabled: formState.recaptchaIsEnabled,
      validToken: formState.recaptchaIsValid,
    });

    if (!formState.recaptchaIsValid) {
      setIsInvalidCaptchaOpen(true);
    } else {
      setIsOpen(formState.isSuccess ?? false);
    }
  }, [formState]);
  const {
    firstName,
    lastName,
    address,
    zip,
    city,
    phone,
    email,
    message,
    time,
    date,
  } = formState?.data || {};

  const { cmsData, app, confirmDescription, confirmTitle } = config;

  return (
    <div id="assessment-form">
      <ConfirmModal
        description={confirmDescription ?? "Tak for din bestilling"}
        title={confirmTitle ?? "Tak for din bestilling"}
        setOpen={(isOpen) => setIsOpen(isOpen)}
        isOpen={isOpen}
        contactInfo={{
          phone: formatPhoneNumberObject(formState.contactInfo?.phone ?? ""),
          email: formState.contactInfo?.email?.address ?? "",
        }}
      />
      <ConfirmModal
        modalType="error"
        description="Prøv venligst igen. Hvis du fortsætter med at opleve denne fejl så kontakt venligst support."
        title="Din forespørgsel blev blokeret"
        setOpen={(isOpen) => setIsInvalidCaptchaOpen(isOpen)}
        isOpen={isInvalidCaptchaOpen}
      />
      <FormLayout
        submitLabel="Bestil"
        isPending={isPending}
        featureFlags={cmsData?.featureFlags}
        formAction={submit}
      >
        <input
          type="hidden"
          name="messageTextPre"
          defaultValue={config.messageTextPre}
        />
        <input
          type="hidden"
          name="messageTextPost"
          defaultValue={config.messageTextPost}
        />

        {config.resourceGroupId ? (
          <input
            type="hidden"
            name="resourceGroupId"
            value={config.resourceGroupId}
          />
        ) : (
          <StoreSelect
            cmsData={cmsData}
            label="Vælg butik"
            name="resourceGroupId"
            stores={app.stores ?? []}
          />
        )}
        <DateInput
          cmsData={cmsData}
          name="date"
          label="Dato"
          defaultValue={date}
          errors={formState?.zodErrors?.date}
          required={true}
        />
        <TextInput
          cmsData={cmsData}
          name="time"
          label="Ønsket tidspunkt på dagen"
          defaultValue={time}
          errors={formState?.zodErrors?.time}
          required={false}
        />
        <TextInput
          cmsData={cmsData}
          name="firstName"
          label="Fornavn"
          defaultValue={firstName}
          errors={formState?.zodErrors?.firstName}
          required={cmsData?.forms.nameRequired}
        />
        <TextInput
          cmsData={cmsData}
          name="lastName"
          label="Efternavn"
          defaultValue={lastName}
          errors={formState?.zodErrors?.lastName}
          required={cmsData?.forms.nameRequired}
        />
        <TextInput
          cmsData={cmsData}
          className="col-span-6"
          name="address"
          label="Adresse"
          defaultValue={address}
          errors={formState?.zodErrors?.address}
          required={cmsData?.forms.addressRequired}
        />
        <TextInput
          cmsData={cmsData}
          className="col-span-2"
          name="zip"
          label="Postnr"
          defaultValue={zip}
          errors={formState?.zodErrors?.zip}
          required={cmsData?.forms.zipRequired}
        />
        <TextInput
          cmsData={cmsData}
          className="col-span-4"
          name="city"
          label="By"
          defaultValue={city}
          errors={formState?.zodErrors?.city}
          required={cmsData?.forms.cityRequired}
        />
        <TextInput
          cmsData={cmsData}
          className="col-span-3"
          name="phone"
          label="Telefonnummer"
          defaultValue={phone}
          errors={formState?.zodErrors?.phone}
          required={cmsData?.forms.phoneRequired}
        />
        <TextInput
          cmsData={cmsData}
          className="col-span-3"
          name="email"
          label="E-mail"
          defaultValue={email}
          errors={formState?.zodErrors?.email}
          required={cmsData?.forms.emailRequired}
        />
        <TextAreaInput
          cmsData={cmsData}
          name="message"
          label="Besked"
          defaultValue={message}
          errors={formState?.zodErrors?.message}
          required={false}
        />
      </FormLayout>
    </div>
  );
};

export default AssessmentForm;
